import React, { useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Reveal } from './MobileSections';
import { useSnapIndex, Dots } from './MobileGallery';

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-9 h-9" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6C12.3 13.4 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z" />
    <path fill="#FBBC05" d="M10.4 28.8A14.5 14.5 0 0 1 9.5 24c0-1.7.3-3.3.9-4.8l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6z" />
    <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.5-5.6l-7.6-5.9c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-3.9-13.6-9.7l-7.8 6C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

const Stars = ({ n = 5, size = 'w-4 h-4' }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`${size} ${i < n ? 'fill-[#F5B301] text-[#F5B301]' : 'text-slate-300'}`} />)}
  </div>
);

export default function MobileReviews({ data }) {
  const ref = useRef(null);
  const reviews = data.reviews.slice(0, 6);
  const index = useSnapIndex(ref, reviews.length);

  const scrollBy = (d) => {
    const el = ref.current;
    if (!el?.firstElementChild) return;
    el.scrollBy({ left: d * (el.firstElementChild.offsetWidth + 12), behavior: 'smooth' });
  };

  return (
    <section data-testid="mobile-reviews" className="bg-white py-14">
      <Reveal className="px-5">
        <h2 className="text-[30px] font-black tracking-[-0.02em] text-[#111827]">What Our Customers Say</h2>
        <a href={data.maps_url || '#'} target="_blank" rel="noopener noreferrer" className="mt-5 flex items-center gap-4 rounded-2xl bg-[#F4F6FA] p-4" data-testid="google-rating-badge">
          <GoogleG />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[28px] font-black text-[#111827] leading-none">{Number(data.rating).toFixed(1)}</span>
              <Stars size="w-5 h-5" />
            </div>
            <div className="mt-1 text-[13px] text-[#5F6773] font-medium">
              on Google{data.total_reviews ? ` · Based on ${data.total_reviews} reviews` : ''}
            </div>
          </div>
        </a>
      </Reveal>

      <div ref={ref} className="mt-6 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 scroll-px-5" data-testid="reviews-scroller">
        {reviews.map((r, i) => (
          <article key={i} className="snap-start shrink-0 w-[82vw] max-w-[360px] rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.07)] flex flex-col" data-testid={`review-card-${i}`}>
            <Quote className="w-6 h-6 text-[#0D5BD7]/30 fill-[#0D5BD7]/20" />
            <p className="mt-3 text-[15px] text-[#111827] leading-[1.6] line-clamp-5 flex-1">{r.text}</p>
            <div className="mt-5 flex items-center gap-3">
              {r.author_photo ? (
                <img src={r.author_photo} alt={r.author} referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-[#0D5BD7] text-white font-bold flex items-center justify-center">{r.author?.[0]}</div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-bold text-[#111827] truncate">{r.author}</span>
                  <Stars n={r.rating || 5} size="w-3.5 h-3.5" />
                </div>
                <div className="text-[12px] text-[#5F6773]">{r.location || r.time_ago || 'Google review'}</div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-5 px-5 flex items-center justify-between">
        <button onClick={() => scrollBy(-1)} aria-label="Previous review" data-testid="reviews-prev-btn" className="w-11 h-11 rounded-full border-2 border-slate-200 flex items-center justify-center text-[#111827] active:bg-slate-50"><ChevronLeft className="w-5 h-5" /></button>
        <Dots count={reviews.length} index={index} />
        <button onClick={() => scrollBy(1)} aria-label="Next review" data-testid="reviews-next-btn" className="w-11 h-11 rounded-full border-2 border-slate-200 flex items-center justify-center text-[#111827] active:bg-slate-50"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </section>
  );
}
